import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { SearchSelectOption } from "./SearchSelect";

/**
 * Variante multi-selección del `SearchSelect`: permite marcar varias opciones a
 * la vez (checkboxes) manteniendo el buscador y el panel en portal con posición
 * fija, por lo que nunca queda recortado por contenedores con `overflow`.
 */
interface MultiSearchSelectProps {
  value: string[];
  options: SearchSelectOption[];
  onChange: (codes: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

interface PanelCoords {
  left: number;
  top: number;
  width: number;
  placement: "down" | "up";
}

const PANEL_MAX_HEIGHT = 300;

const MultiSearchSelect = ({
  value,
  options,
  onChange,
  placeholder = "Seleccionar",
  searchPlaceholder = "Buscar",
  disabled = false,
  ariaLabel,
}: MultiSearchSelectProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState<PanelCoords>();

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedOptions = useMemo(
    () => options.filter((option) => selectedSet.has(option.code)),
    [options, selectedSet]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.name.toLowerCase().includes(q));
  }, [options, query]);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: "down" | "up" =
      spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow ? "up" : "down";
    setCoords({
      left: rect.left,
      width: rect.width,
      top: placement === "down" ? rect.bottom + 4 : rect.top - 4,
      placement,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onScroll = (event: Event) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      place();
    };
    const onResize = () => place();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, place]);

  const toggle = () => {
    if (disabled) return;
    setQuery("");
    setOpen((current) => !current);
  };

  const toggleOption = (code: string) => {
    if (selectedSet.has(code)) {
      onChange(value.filter((item) => item !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const clearAll = () => onChange([]);

  const triggerLabel = (): string => {
    if (selectedOptions.length === 0) return "";
    if (selectedOptions.length === 1) return selectedOptions[0].name;
    return `${selectedOptions.length} clientes seleccionados`;
  };

  return (
    <div className="inhab-nomina__ss">
      <button
        type="button"
        ref={triggerRef}
        className={`inhab-nomina__ss-trigger ${open ? "is-open" : ""}`}
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="inhab-nomina__ss-value">
          {selectedOptions.length ? (
            triggerLabel()
          ) : (
            <em>{placeholder}</em>
          )}
        </span>
        {selectedOptions.length > 0 && !disabled && (
          <span
            className="inhab-nomina__ss-clear"
            role="button"
            aria-label="Quitar selección"
            onClick={(event) => {
              event.stopPropagation();
              clearAll();
            }}
          >
            <CloseIcon />
          </span>
        )}
        <KeyboardArrowDownIcon className="inhab-nomina__ss-arrow" />
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            className={`inhab-nomina__ss-panel ${
              coords.placement === "up" ? "is-up" : ""
            }`}
            style={{
              position: "fixed",
              left: coords.left,
              width: coords.width,
              top: coords.top,
              transform:
                coords.placement === "up" ? "translateY(-100%)" : undefined,
            }}
          >
            <div className="inhab-nomina__ss-search">
              <SearchIcon />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
            <div className="inhab-nomina__ss-options" role="listbox">
              {filtered.map((option) => {
                const active = selectedSet.has(option.code);
                return (
                  <button
                    type="button"
                    key={option.code}
                    role="option"
                    aria-selected={active}
                    className={`inhab-nomina__ss-option ${
                      active ? "is-active" : ""
                    }`}
                    onClick={() => toggleOption(option.code)}
                  >
                    {active ? (
                      <CheckBoxIcon />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )}
                    <span>{option.name}</span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="inhab-nomina__ss-empty">
                  {query ? "No hay resultados." : "Sin opciones."}
                </div>
              )}
            </div>
            {selectedOptions.length > 0 && (
              <div className="inhab-nomina__ss-footer">
                <span>{selectedOptions.length} seleccionados</span>
                <button type="button" onClick={clearAll}>
                  Limpiar
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default MultiSearchSelect;
